import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { signUpWithEmail, signInWithGoogle } from '../../services/authService';
import { FirebaseError } from 'firebase/app';
import GoogleIcon from '../../assets/google-icon.svg';
import { getPasswordRequirementChecks } from '../../utils/passwordValidation';
import './Auth.css';

interface SignUpProps {
  onClose: () => void;
  onSwitchToSignIn: () => void;
  onSignUpSuccess?: () => void;
}

export default function SignUp({ onClose, onSwitchToSignIn, onSignUpSuccess }: SignUpProps) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    displayName: Yup.string()
      .required(t('auth.errors.displayNameRequired') || 'Display name is required')
      .min(2, t('auth.errors.displayNameTooShort') || 'Display name must be at least 2 characters'),
    email: Yup.string()
      .email(t('auth.errors.invalidEmail') || 'Invalid email address')
      .required(t('auth.errors.emailRequired') || 'Email is required'),
    password: Yup.string()
      .required(t('auth.errors.passwordRequired') || 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: Yup.string()
      .required(t('auth.errors.confirmPasswordRequired') || 'Please confirm your password')
      .oneOf([Yup.ref('password')], t('auth.errors.passwordMismatch') || 'Passwords must match'),
  });

  const formik = useFormik({
    initialValues: {
      displayName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setError('');
      setLoading(true);

      try {
        await signUpWithEmail(values.email, values.password, values.displayName);
        console.log('Sign up successful for user.');
        if (onSignUpSuccess) {
          onSignUpSuccess();
        } else {
          onClose();
        }
      } catch (err) {
        console.error('Sign up failed:', err);
        if (err instanceof FirebaseError) {
          console.error('Firebase error code:', err.code);
          console.error('Firebase error message:', err.message);
          switch (err.code) {
            case 'auth/email-already-in-use':
              setError(t('auth.errors.emailInUse'));
              break;
            case 'auth/invalid-email':
              setError(t('auth.errors.invalidEmail'));
              break;
            case 'auth/weak-password':
              setError(t('auth.errors.weakPassword'));
              break;
            default:
              setError(t('auth.errors.signUpFailed'));
          }
        } else {
          console.error('Non-Firebase error:', err);
          setError(t('auth.errors.signUpFailed'));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const passwordChecks = getPasswordRequirementChecks(formik.values.password);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();
      console.log('Google sign up successful');
      if (onSignUpSuccess) {
        onSignUpSuccess();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Google sign up failed:', err);
      if (err instanceof FirebaseError) {
        console.error('Firebase error code:', err.code);
        console.error('Firebase error message:', err.message);
        switch (err.code) {
          case 'auth/popup-closed-by-user':
            setError(t('auth.errors.popupClosed'));
            break;
          case 'auth/popup-blocked':
            setError(t('auth.errors.popupBlocked'));
            break;
          default:
            setError(t('auth.errors.googleSignInFailed'));
        }
      } else {
        console.error('Non-Firebase error:', err);
        setError(t('auth.errors.googleSignInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-form">
      {error && <div className="auth-error">{error}</div>}

      <form onSubmit={formik.handleSubmit}>
        <div className="form-group">
          <label htmlFor="displayName">{t('auth.displayName')}</label>
          <input
            type="text"
            id="displayName"
            name="displayName"
            value={formik.values.displayName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
            placeholder={t('auth.displayNamePlaceholder')}
          />
          {formik.touched.displayName && formik.errors.displayName && (
            <span className="error-message">{formik.errors.displayName}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">{t('auth.email')}</label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
            placeholder={t('auth.emailPlaceholder')}
          />
          {formik.touched.email && formik.errors.email && (
            <span className="error-message">{formik.errors.email}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('auth.password')}</label>
          <input
            type="password"
            id="password"
            name="password"
            autoComplete="new-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
            placeholder={t('auth.passwordPlaceholder')}
          />
          {formik.values.password && (
            <div className="password-requirements">
              <p className="requirements-title">{t('auth.passwordRequirements')}</p>
              <ul className="requirements-list">
                {passwordChecks.map((check, index) => (
                  <li key={index} className={check.met ? 'requirement-met' : 'requirement-unmet'}>
                    <span className="requirement-icon">{check.met ? '✓' : '○'}</span>
                    {check.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {formik.touched.password && formik.errors.password && (
            <span className="error-message">{formik.errors.password}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
            placeholder={t('auth.confirmPasswordPlaceholder')}
          />
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <span className="error-message">{formik.errors.confirmPassword}</span>
          )}
        </div>

        <button type="submit" className="auth-submit-button" disabled={loading}>
          {loading ? t('auth.signingUp') : t('auth.signUp')}
        </button>
      </form>

      <div className="auth-divider">
        <span>{t('auth.orContinueWith')}</span>
      </div>

      <button
        type="button"
        className="auth-google-button"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        <img src={GoogleIcon} alt="Google" className="google-icon" />
        {t('auth.continueWithGoogle')}
      </button>

      <div className="auth-footer">
        <p>
          {t('auth.haveAccount')}{' '}
          <button type="button" className="auth-link" onClick={onSwitchToSignIn}>
            {t('auth.signIn')}
          </button>
        </p>
      </div>
    </div>
  );
}
